import React from "react"
import styled, {keyframes} from "styled-components"
import {Project, ProjectType, LinkType} from "../../consts/projects";
import TabletMockup from "../Mockups/TabletMockup";
import PhoneMockup from "../Mockups/PhoneMockup";
import ReactGA from 'react-ga';

interface Props {
    project: Project
    index?: number
}

const Reference = (props: Props) => {
    const {project, index = 0} = props

    const handleClick = () => {
        ReactGA.event({
            category: 'Reference-Click',
            action: project.name
        });
    }

    const isReversed = index % 2 === 1

    return (
        <Card style={{animationDelay: `${index * 0.08}s`}}>
            <CardInner reversed={isReversed}>
                <Visual>
                    <BgImage
                        style={{
                            backgroundImage: `url(${project.bgUrl})`,
                            backgroundPosition: project.bgPos || 'center'
                        }}
                    />
                    <BgOverlay />
                    <MockupFloat>
                        {project.type === ProjectType.TABLET ? (
                            <TabletMockup project={project}/>
                        ) : (
                            <PhoneMockup project={project}/>
                        )}
                    </MockupFloat>
                </Visual>

                <Info>
                    <ProjectIndex>{String(index + 1).padStart(2, '0')}</ProjectIndex>
                    <ProjectName>{project.name}</ProjectName>
                    <ProjectDesc>{project.sub}</ProjectDesc>

                    <Links>
                        {project.linkType === LinkType.EXTERNAL && project.urls.android && (
                            <a href={project.urls.android} target="_blank" rel="noopener noreferrer" onClick={handleClick}>
                                <GoogleBadge/>
                            </a>
                        )}
                        {project.linkType === LinkType.EXTERNAL && project.urls.web && (
                            <a href={project.urls.web} target="_blank" rel="noopener noreferrer" onClick={handleClick}>
                                <WebLink>
                                    <i className="fa-solid fa-arrow-up-right-from-square" />
                                    {project.urls.webText || "Navštívit web"}
                                </WebLink>
                            </a>
                        )}
                        {project.linkType === LinkType.INTERNAL && (
                            <StatusBadge>
                                <i className="fa-solid fa-lock" />
                                Interní projekt
                            </StatusBadge>
                        )}
                        {project.linkType === LinkType.NO_BUTTON && (
                            <StatusBadge>
                                <i className="fa-solid fa-clock-rotate-left" />
                                Projekt ukončen
                            </StatusBadge>
                        )}
                    </Links>
                </Info>
            </CardInner>
        </Card>
    )
}

export default Reference

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const Card = styled.div`
  animation: ${fadeUp} 0.6s ease both;
  margin-bottom: 4em;

  @media (max-width: 799px) {
    margin-bottom: 3em;
  }
`

interface CardInnerProps {
    reversed: boolean
}

const CardInner = styled.div<CardInnerProps>`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 3em;
  align-items: center;
  direction: ${({reversed}) => reversed ? 'rtl' : 'ltr'};

  & > * {
    direction: ltr;
  }

  @media (max-width: 799px) {
    grid-template-columns: 1fr;
    gap: 1.5em;
    direction: ltr;
  }
`

const Visual = styled.div`
  position: relative;
  border-radius: 0.5em;
  overflow: hidden;
  aspect-ratio: 16 / 10;
  background: #1a1a1a;

  @media (max-width: 799px) {
    aspect-ratio: 16 / 9;
  }
`

const BgImage = styled.div`
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  transition: transform 0.5s ease;

  ${Visual}:hover & {
    transform: scale(1.04);
  }
`

const BgOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.3) 0%,
    rgba(0, 0, 0, 0.1) 50%,
    rgba(0, 0, 0, 0.4) 100%
  );
`

const MockupFloat = styled.div`
  position: absolute;
  bottom: -12%;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.75em;
  filter: drop-shadow(0 8px 30px rgba(0, 0, 0, 0.3));
  transition: transform 0.4s ease;
  max-height: 110%;

  ${Visual}:hover & {
    transform: translateX(-50%) translateY(-4px);
  }

  @media (max-width: 1100px) {
    font-size: 0.65em;
  }

  @media (max-width: 799px) {
    font-size: 0.5em;
    bottom: -8%;
  }

  @media (max-width: 500px) {
    font-size: 0.38em;
    bottom: -5%;
  }
`

const Info = styled.div`
  padding: 1em 0;

  @media (max-width: 799px) {
    padding: 0.5em 0;
  }
`

const ProjectIndex = styled.span`
  display: block;
  font-size: 0.75em;
  font-weight: 400;
  letter-spacing: 0.1em;
  opacity: 0.25;
  margin-bottom: 0.8em;
`

const ProjectName = styled.h3`
  font-size: 1.8em;
  font-weight: 800;
  margin: 0 0 0.3em;
  letter-spacing: -0.02em;
  line-height: 1.15;

  @media (max-width: 500px) {
    font-size: 1.4em;
  }
`

const ProjectDesc = styled.p`
  font-size: 0.92em;
  line-height: 1.65;
  margin: 0 0 1.5em;
  opacity: 0.5;
  font-weight: 300;
  max-width: 24em;
`

const Links = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6em;
  align-items: center;

  a {
    text-decoration: none;
    color: inherit;
  }
`

const WebLink = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
  background: rgba(25, 25, 25, 1);
  color: #fff;
  padding: 0.6em 1.2em;
  border-radius: 0.35em;
  font-size: 0.85em;
  font-weight: 500;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease;

  i {
    font-size: 0.8em;
  }

  &:hover {
    transform: translateY(-1px);
    background: rgba(25, 25, 25, 0.85);
  }
`

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
  background: #ebebeb;
  color: rgba(25, 25, 25, 0.6);
  padding: 0.6em 1.2em;
  border-radius: 0.35em;
  font-size: 0.85em;
  font-weight: 500;
  letter-spacing: 0.02em;

  i {
    font-size: 0.8em;
  }
`

const GoogleBadge = styled.div`
  background-image: url("/img/en_badge_web_generic.png");
  width: 10em;
  height: 3.2em;
  background-size: cover;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }
`
